import React, { useState } from 'react';
import { Card, Table, Button, Spinner, Pagination } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Get, DeleteSingle, Put } from '@app/utils/httpProvider';
import { VANBANG_API, BaseGatewayImage, ACCOUNT_AUTHINFO_API, ACCOUNT_API } from '@app/utils/apiUrl';
import { getUser } from '@app/utils/oidc';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import { URI_INTERNAL } from '../../../../utils/pathLocations';

// Helper Format Ngày
const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('vi-VN');
};

// Helper Format Trạng thái
const formatTrangThai = (status) => {
    if (status === null || status === undefined || status === '') {
        return { label: 'Chưa cập nhật', color: 'bg-secondary' };
    }
    const strStatus = String(status).toLowerCase().trim();
    switch (strStatus) {
        case '0':
        case 'nhaplieu': return { label: 'Nhập liệu', color: 'bg-secondary' };
        case '1':
        case 'choduyet': return { label: 'Chờ duyệt', color: 'bg-warning text-dark' };
        case '2':
        case 'daduyet': return { label: 'Đã duyệt', color: 'bg-success' };
        case '3':
        case 'tuchoi': return { label: 'Từ chối', color: 'bg-danger' };
        default: return { label: 'Chờ duyệt', color: 'bg-warning text-dark' };
    }
};

// Helper Format Loại văn bằng
const formatLoaiVanBang = (type) => {
    if (!type) return '-';
    const strType = String(type).trim();
    if (strType === 'NuocNgoai') return 'Văn bằng nước ngoài';
    if (strType === 'VanBangTrongNuoc') return 'Văn bằng trong nước (ngoại ngữ)'; // Ví dụ thêm loại văn bằng mới   
    return strType;
};

const VanBang = () => {
    
    // State quản lý phân trang
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // Gán cứng số bản ghi trên trang là 10

    const [isSubmitting, setIsSubmitting] = useState(false);

    // State quản lý modal xác nhận gửi
    const [openConfirmGuiXacNhan, setOpenConfirmGuiXacNhan] = useState(false);
    const [idWantGuiXacNhan, setIdWantGuiXacNhan] = useState('');

    // State quản lý hover
    const [isHovered, setIsHovered] = useState(false);

    // Fetch dữ liệu tổng hợp (Họ tên, ngày sinh, chức danh, khoa...)
    const fetchFullProfile = async () => {
        try {
            const user = await getUser();
            const accountId = user?.profile?.sub;
            if (!accountId) return null;

            const [authRes, detailRes] = await Promise.all([
                Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${accountId}`),
                Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accountId}`)
            ]);

            return {
                auth: authRes?.data || authRes,
                detail: detailRes?.data || detailRes
            };
        } catch (error) {
            console.error('Lỗi lấy thông tin tổng hợp hồ sơ:', error);
            return null;
        }
    };

    const { data: fullProfile, isLoading: isLoadingProfile } = useQuery(
        ['fullProfile'],
        fetchFullProfile,
        { cacheTime: 0, staleTime: 0 }
    );

    const auth = fullProfile?.auth;
    const detail = fullProfile?.detail;

    const fullName = auth ? [
        auth.lastName || auth.LastName,
        auth.middleName || auth.MiddleName,
        auth.firstName || auth.FirstName
    ].filter(v => v !== null && v !== undefined && String(v).trim() !== "").join(' ') : (isLoadingProfile ? '...' : '-');

    const dob = auth?.dateOfBirth || auth?.DateOfBirth;
    const yearOfBirth = dob ? new Date(dob).getFullYear() : (isLoadingProfile ? '...' : '-');

    const chucDanh = detail?.doiTuong?.tenDoiTuong || detail?.DoiTuong?.TenDoiTuong || '-';
    const khoa = detail?.donVis?.[0]?.tenDonVi || detail?.DonVis?.$values?.[0]?.TenDonVi || '-';

    // Fetch dữ liệu danh sách văn bằng
    const fetchVanBangList = async () => {
        try {
            const user = await getUser();
            const accountId = user?.profile?.sub;

            if (!accountId) return { items: [], total: 0 };

            const response = await Get(`${VANBANG_API.GET_BY_ACCOUNT_ID}/${accountId}`);
            const data = response?.data || response;

            const items = data?.$values || data?.data || (Array.isArray(data) ? data : []);
            const total = data?.totalCount || data?.total || items.length;

            return {
                items: Array.isArray(items) ? items : [],
                total: total
            };
        } catch (error) {
            console.error('Lỗi lấy dữ liệu danh sách văn bằng:', error);
            throw error;
        }
    };

    const { isLoading, isError, error, data: listData, refetch } = useQuery(
        ['vanBangList', currentPage, itemsPerPage],
        fetchVanBangList,
        {
            enabled: true,
            refetchOnMount: true,
            cacheTime: 0
        }
    );

    const vanBangList = listData?.items || [];
    const totalRecords = listData?.total || 0;
    const totalPages = Math.ceil(totalRecords / itemsPerPage);

    const handleOpenFile = (path) => {
        if (path) {
            window.open(`${BaseGatewayImage}${path}`, '_blank');
        }
    };



    const openModalConfirmGuiXacNhan = (id) => {
        setIdWantGuiXacNhan(id);
        setOpenConfirmGuiXacNhan(true);
    };

    const cancelGuiXacNhan = () => {
        setIdWantGuiXacNhan('');
        setOpenConfirmGuiXacNhan(false);
    };

    const acceptGuiXacNhan = async () => {
        if (!idWantGuiXacNhan) return;

        try {
            setIsSubmitting(true);
            setOpenConfirmGuiXacNhan(false);

            const payload = { id: idWantGuiXacNhan, fieldName: 'TrangThai', value: 1 }; // ChoDuyet = 1
            await Put(VANBANG_API.UPDATE_FIELD, null, payload);

            toast.success('Gửi xác nhận văn bằng thành công!');
            await refetch();
        } catch (err) {
            console.error('Lỗi khi gửi xác nhận:', err);
            toast.error('Có lỗi xảy ra khi gửi xác nhận.');
        } finally {
            setIsSubmitting(false);
            setIdWantGuiXacNhan('');
        }
    };

    const getPaginationItems = () => {
        const items = [];
        const maxPages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
        let endPage = Math.min(totalPages, startPage + maxPages - 1);

        if (endPage - startPage < maxPages - 1) {
            startPage = Math.max(1, endPage - maxPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(i);
        }

        return items;
    };

    return (
        <div className="container-fluid">
            <style>
                {`
                .custom-group-table tbody tr > td {
                    background-color: #ffffff !important;
                    box-shadow: none !important;
                    transition: background-color 0.15s ease-in-out;
                }
                
                .custom-group-table tbody tr.table-row-hover td {
                    background-color: #f5f6f8 !important;
                }
                `}
            </style>

            <Card style={{ borderRadius: '16px' }}>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Danh sách Văn bằng</h5>
                        <NavLink to={URI_INTERNAL.VANBANG_CREATE_PATH} className="btn btn-success">
                            Thêm mới
                        </NavLink>
                    </div>
                </Card.Header>

                <Card.Body>
                    <div className="table-responsive">
                        <Table bordered className="align-middle custom-group-table mb-0" style={{ border: '1px solid #dee2e6' }}>
                            <thead className="table-light">
                                <tr>
                                    <th style={{ border: '1px solid #dee2e6', width: '50px', minWidth: '50px' }} className="text-center align-middle">STT</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '150px' }} className="text-center align-middle">Họ và tên</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '100px' }} className="text-center align-middle">Năm sinh</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '120px' }} className="text-center align-middle">Chức danh</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '120px' }} className="text-center align-middle">Khoa</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '160px' }} className="text-center align-middle">Tên văn bằng</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '120px' }} className="text-center align-middle">Loại văn bằng</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '140px' }} className="text-center align-middle">Chuyên ngành</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '150px' }} className="text-center align-middle">Cơ sở cấp bằng</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '110px' }} className="text-center align-middle">Quốc gia</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '110px' }} className="text-center align-middle">Số hiệu</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '110px' }} className="text-center align-middle">Ngày cấp</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '100px' }} className="text-center align-middle">Xếp loại</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '100px' }} className="text-center align-middle">Hợp pháp hóa</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '100px' }} className="text-center align-middle">File minh chứng</th>
                                    <th style={{ border: '1px solid #dee2e6', minWidth: '100px' }} className="text-center align-middle">Trạng thái</th>
                                    <th style={{ border: '1px solid #dee2e6', width: '130px' }} className="text-center align-middle">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="17" className="text-center py-4 align-middle" style={{ border: '1px solid #dee2e6' }}>
                                            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                            Đang tải dữ liệu...
                                        </td>
                                    </tr>
                                ) : isError ? (
                                    <tr>
                                        <td colSpan="17" className="text-center text-danger py-4 align-middle" style={{ border: '1px solid #dee2e6' }}>
                                            Lỗi: {error?.message || 'Không tải được dữ liệu'}
                                        </td>
                                    </tr>
                                ) : vanBangList.length === 0 ? (
                                    <tr>
                                        <td colSpan="17" className="text-center py-4 align-middle" style={{ border: '1px solid #dee2e6' }}>
                                            Không có dữ liệu
                                        </td>
                                    </tr>
                                ) : (
                                    vanBangList.map((item, index) => {
                                        const trangThaiInfo = formatTrangThai(item.trangThai || item.TrangThai);
                                        const stt = (currentPage - 1) * itemsPerPage + 1; // Giữ STT cố định cho nhóm
                                        const rowId = item.id || item.Id || item.vanBangId || item.VanBangId;
                                        
                                        const isFirstRow = index === 0;
                                        const rowSpan = vanBangList.length;

                                        return (
                                            <tr 
                                                key={rowId || index}
                                                onMouseEnter={() => setIsHovered(true)}
                                                onMouseLeave={() => setIsHovered(false)}
                                                className={isHovered ? 'table-row-hover' : ''}
                                            >
                                                {isFirstRow && (
                                                    <>
                                                        <td rowSpan={rowSpan} className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{stt}</td>
                                                        <td rowSpan={rowSpan} className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{fullName}</td>
                                                        <td rowSpan={rowSpan} className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{yearOfBirth}</td>
                                                        <td rowSpan={rowSpan} className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{chucDanh}</td>
                                                        <td rowSpan={rowSpan} className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{khoa}</td>
                                                    </>
                                                )}
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{item.tenVanBang || item.TenVanBang || '-'}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{formatLoaiVanBang(item.loaiVanBang || item.LoaiVanBang)}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{item.chuyenNganh || item.ChuyenNganh || '-'}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{item.coSoCapBang || item.CoSoCapBang || '-'}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{item.quocGiaCap || item.QuocGiaCap || '-'}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{item.soHieuVanBang || item.SoHieuVanBang || '-'}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{formatDate(item.ngayCap || item.NgayCap)}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>{item.xepLoai || item.XepLoai || '-'}</td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>
                                                    {(item.isHopPhapHoa || item.IsHopPhapHoa) ? (
                                                        <span className="badge bg-success">Có</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">Không</span>
                                                    )}
                                                </td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>
                                                    {item.fileMinhChungId || item.FileMinhChungId ? (
                                                        <Button
                                                            variant="outline-info"
                                                            size="sm"
                                                            onClick={() => handleOpenFile(item.FileMinhChungPath || item.fileMinhChungPath)}
                                                        >
                                                            Xem
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>
                                                    <span className={`badge ${trangThaiInfo.color}`}>
                                                        {trangThaiInfo.label}
                                                    </span>
                                                    {(item.trangThai === 3 || item.TrangThai === 3 || String(item.trangThai).toLowerCase() === 'tuchoi' || String(item.TrangThai).toLowerCase() === 'tuchoi') && (
                                                        <div className="text-danger mt-1" style={{ fontSize: '0.75rem', maxWidth: '150px', margin: '0 auto', wordBreak: 'break-word' }}>
                                                            {item.lyDoKhongDuyet || item.LyDoKhongDuyet || item.lyDo || item.LyDo ? `(${item.lyDoKhongDuyet || item.LyDoKhongDuyet || item.lyDo || item.LyDo})` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-center align-middle" style={{ border: '1px solid #dee2e6' }}>
                                                    <div className="btn-group" role="group" style={{ gap: '4px', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                                                        {trangThaiInfo.label !== 'Đã duyệt' && (
                                                            <NavLink
                                                                to={`${URI_INTERNAL.VANBANG_EDIT_PATH}?id=${rowId}`}
                                                                className="btn btn-primary btn-sm"
                                                                title="Chỉnh sửa"
                                                            >
                                                                Sửa
                                                            </NavLink>
                                                        )}
                                                        {trangThaiInfo.label === 'Nhập liệu' && (
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => openModalConfirmGuiXacNhan(rowId)}
                                                                disabled={isSubmitting}
                                                                title="Gửi xác nhận"
                                                            >
                                                                {isSubmitting ? '...' : 'Gửi xác nhận'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && !isLoading && vanBangList.length > 0 && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} />
                                {currentPage > 1 && <Pagination.Ellipsis disabled />}
                                {getPaginationItems().map(page => (
                                    <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                                        {page}
                                    </Pagination.Item>
                                ))}
                                {currentPage < totalPages && <Pagination.Ellipsis disabled />}
                                <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
                                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}

                    {!isLoading && vanBangList.length > 0 && (
                        <div className="text-center mt-3 text-muted">
                            Hiển thị {((currentPage - 1) * itemsPerPage) + 1} đến {Math.min(currentPage * itemsPerPage, totalRecords)} trong {totalRecords} bản ghi
                        </div>
                    )}
                </Card.Body>
            </Card>



            <ConfirmModal
                isOpen={openConfirmGuiXacNhan}
                confirmType="confirm"
                handleAccept={acceptGuiXacNhan}
                handleCancel={cancelGuiXacNhan}
                content="Bạn có chắc chắn muốn gửi xác nhận văn bằng này?"
            />
        </div>
    );
};

export default VanBang;